// src/ReferencesPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Loader, Building, Award, Users, Briefcase, CheckCircle, X, Eye } from 'lucide-react';
import { API } from './utils/database-frontend.js';

const ReferencesPage = () => {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReference, setSelectedReference] = useState(null);
  
  const statsRef = useRef(null);
  const referencesRef = useRef(null);
  
  const isStatsInView = useInView(statsRef, { once: true, threshold: 0.2 });
  const isReferencesInView = useInView(referencesRef, { once: true, threshold: 0.1 });

  const stats = [
    {
      icon: Building,
      title: "Tamamlanan Projeler",
      count: "250+",
      description: "Başarıyla tamamlanan projeler",
      gradient: "from-brand-orange to-orange-500"
    },
    {
      icon: Award,
      title: "Memnun Müşteri",
      count: "100+",
      description: "Güvenen ve tavsiye eden firmalar",
      gradient: "from-brand-navy to-blue-600"
    },
    {
      icon: Users,
      title: "İş Ortaklığı",
      count: "50+",
      description: "Uzun vadeli iş ortaklıkları",
      gradient: "from-brand-gray to-gray-600"
    },
    {
      icon: Briefcase,
      title: "Sektör Deneyimi",
      count: "20+",
      description: "Yıllık sektör tecrübesi",
      gradient: "from-brand-orange to-red-500"
    }
  ];

  // Fetch references from database
  useEffect(() => {
    const fetchReferences = async () => {
      try {
        setLoading(true);
        const fetchedReferences = await API.references.getAll();
        console.log('Fetched references from API:', fetchedReferences);
        setReferences(fetchedReferences);
        setError(null);
      } catch (err) {
        console.error('Error fetching references:', err);
        setError('Referanslar yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        setReferences([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReferences();
  }, []);

  const openModal = (reference) => {
    setSelectedReference(reference);
  };

  const closeModal = () => {
    setSelectedReference(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-gradient-to-br from-brand-navy via-brand-gray to-brand-navy text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-40"></div>
        
        {/* Animated Background Shapes */}
        <motion.div
          animate={{ 
            rotate: 360,
            scale: [1, 1.4, 1]
          }}
          transition={{ 
            duration: 35,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-20 right-20 w-56 h-56 bg-brand-orange opacity-10 rounded-full"
        />
        <motion.div
          animate={{ 
            rotate: -360,
            scale: [1, 1.2, 1]
          }}
          transition={{ 
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-20 left-20 w-40 h-40 bg-white opacity-5 rounded-full"
        />

        <div className="relative z-10 text-center px-4 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8"
          >
            <div className="inline-flex items-center justify-center w-24 h-24 bg-brand-orange rounded-full mb-6">
              <Building className="w-12 h-12 text-white" />
            </div>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent"
          >
            Referanslarımız
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            <span className="text-brand-orange font-semibold">Güvenen ve bize değer veren </span> 
            prestijli müşterilerimiz.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="flex flex-wrap justify-center gap-4"
          >

          </motion.div>
        </div>
      </section>



      {/* References Section */}
      <section ref={referencesRef} className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={isReferencesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-brand-navy mb-4">Müşteri Portföyümüz</h2>
            <p className="text-xl text-brand-gray max-w-2xl mx-auto">
              Bize güvenen ve birlikte başarılı projeler hayata geçirdiğimiz değerli müşterilerimiz
            </p>
          </motion.div>

          {/* Loading State */}
          {loading && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-20"
            >
              <div className="relative">
                <Loader className="w-12 h-12 text-brand-orange animate-spin" />
                <div className="absolute inset-0 w-12 h-12 border-2 border-brand-orange border-t-transparent rounded-full animate-ping"></div>
              </div>
              <span className="mt-4 text-brand-gray text-lg font-medium">Referanslar yükleniyor...</span>
              <p className="text-brand-gray text-sm mt-2">Müşteri portföyümüzü getiriyoruz</p>
            </motion.div>
          )}

          {/* Error State */}
          {error && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-20"
            >
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-2xl p-8 max-w-md mx-auto">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-red-800 mb-2">Bir Hata Oluştu</h3>
                <p className="text-red-600">{error}</p>
              </div>
            </motion.div>
          )}

          {/* References Grid */}
          {!loading && !error && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {references.length === 0 ? (
                <motion.div 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="col-span-full text-center py-20"
                >
                  <div className="bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl p-12 max-w-md mx-auto">
                    <Building className="w-16 h-16 text-brand-gray mx-auto mb-4 opacity-50" />
                    <h3 className="text-xl font-semibold text-brand-gray mb-2">Henüz Referans Yok</h3>
                  </div>
                </motion.div>
              ) : (
                references.map((reference, index) => (
                  <motion.div
                    key={reference.id}
                    initial={{ opacity: 0, scale: 0.8, y: 30 }}
                    animate={isReferencesInView ? { opacity: 1, scale: 1, y: 0 } : {}}
                    transition={{ delay: index * 0.05, duration: 0.6 }}
                    whileHover={{ y: -5, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer group border border-gray-100"
                    onClick={() => openModal(reference)}
                  >
                    <div className="relative overflow-hidden">
                      <img 
                        src={reference.imageUrl || reference.image_url || 'https://placehold.co/300x200/f4712f/ffffff?text=Referans'} 
                        alt={reference.alt_text || `Referans ${reference.id}`}
                        className="w-full h-32 sm:h-40 object-contain bg-gradient-to-br from-gray-50 to-gray-100 group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          e.target.src = 'https://placehold.co/300x200/f4712f/ffffff?text=Görsel+Bulunamadı';
                        }}
                      />
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
                        <motion.div
                          initial={{ opacity: 0, scale: 0.5 }}
                          whileHover={{ opacity: 1, scale: 1 }}
                          className="bg-white rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        >
                          <Eye className="w-6 h-6 text-brand-orange" />
                        </motion.div>
                      </div>
                      
                      {/* Featured Badge */}
                      {reference.is_featured && (
                        <div className="absolute top-2 right-2 bg-brand-orange text-white px-2 py-1 rounded-full text-xs font-medium">
                          Öne Çıkan
                        </div>
                      )}
                    </div>
                    
                    {/* Reference Info */}
                    {reference.company_name && (
                      <div className="p-3">
                        <h3 className="text-sm font-semibold text-brand-navy group-hover:text-brand-orange transition-colors duration-300 truncate">
                          {reference.company_name}
                        </h3>
                      </div>
                    )}
                  </motion.div>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedReference && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-4xl max-h-[90vh] w-full h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={selectedReference.imageUrl || selectedReference.image_url || 'https://placehold.co/800x600/f4712f/ffffff?text=Referans'} 
                alt={selectedReference.alt_text || `Referans ${selectedReference.id}`}
                className="w-full h-full object-contain rounded-2xl"
                onError={(e) => {
                  e.target.src = 'https://placehold.co/800x600/f4712f/ffffff?text=Görsel+Bulunamadı';
                }}
              />
              
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 bg-white bg-opacity-20 backdrop-blur rounded-full p-3 hover:bg-opacity-30 transition duration-300"
              >
                <X className="w-6 h-6 text-white" />
              </button>
              
              {selectedReference.company_name && (
                <div className="absolute bottom-4 left-4 bg-black bg-opacity-60 backdrop-blur rounded-2xl px-6 py-3">
                  <h3 className="text-white text-lg font-semibold">{selectedReference.company_name}</h3>
                  {selectedReference.is_featured && (
                    <span className="text-brand-orange text-sm">Öne Çıkan Referans</span>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-brand-orange to-orange-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-6">Siz de Referanslarımız Arasında Yer Alın</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto text-orange-100">
              Kaliteli hizmetimizle tanışın ve başarılı projelerinizin bir parçası olalım.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a 
                href="/contact"
                className="inline-block bg-white text-brand-orange font-bold py-4 px-12 rounded-2xl transition duration-300 shadow-lg hover:shadow-xl"
              >
                Hemen İletişime Geç
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ReferencesPage;